// This is the brains of the animal. This is where we'll later change the potential actions.
/* eslint-disable react/sort-comp */

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { INITIAL_STATES } from '../../../constants';

import Hat from '../Goodies/Hat';
import Glasses from '../Goodies/Glasses';

import { setAction } from '../../../utils/setAction';
import getClassNames from '../../../utils/getClassNames';
import getRandom from '../../../utils/getRandom';

let nextId = 0;

class PetHOC extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			universalActions: {
				isBeingPet: false,
				likesPets: Boolean(props.likesPets === 'likes'),
				hatesPets: Boolean(props.likesPets !== 'likes'),
			},
			actions: {
				idle: {},
				pet: {},
			},
			lastActions: {},
			glassesTargetCoords: {},
			hatTargetCoords: {},
		};

		this.id = ++nextId;

		this.timeouts = {
			stopBeingPet: null,
		};

		this.direction = 1;
		this.scale = 0.4;
		this.offset = 0;

		this.staticClasses = ['pet', this.props.petType.name, `fill--${this.props.petType.type}--${this.props.color}`];
		this.actions = {}; // This gets assigned by the presentation component

		this.startBeingPet = this.startBeingPet.bind(this);
		this.stopBeingPet = this.stopBeingPet.bind(this);
		this.getGlassesTargetCoords = this.getGlassesTargetCoords.bind(this);
		this.getHatTargetCoords = this.getHatTargetCoords.bind(this);
	}

	componentDidMount() {
		this.loopAllActions('idle');
	}

	getFrameImagePathname() {
		// if it came from React Router, location will be an object.
		// If not, assume we're in the extension.
		const hasLocation = !!this.props.location;
		return `${hasLocation ? '/' : ''}extension-assets/frame.png`;
	}

	loopAllActions(type) {
		if (type === 'idle') {
			// Idle actions begin after the timeout so that the cat pauses
			this.actions[type].forEach((action) => { this.loopAction(action, type); });
		} else {
			// Pet actions begin right away
			this.actions[type].forEach((action) => { this.setAction(action, type); });
		}
	}

	setAction(action, type) {
		this.setState(setAction(action, this.state, type));
		this.loopAction(action, type);
	}

	loopAction(action, type) {
		const loop = () => {
			clearTimeout(_.find(this.actions[type], ['name', action.name]).start.timeout);
			if (action.stop) clearTimeout(_.find(this.actions[type], ['name', action.name]).stop.timeout);

			_.find(this.actions[type], ['name', action.name]).start.timeout = setTimeout(() => {
				this.setAction(action, type);
			}, action.start.interval());
		};

		// If this action has a time limit, run the stop timeout.
		if (action.stop) {
			clearTimeout(_.find(this.actions[type], ['name', action.name]).stop.timeout);

			_.find(this.actions[type], ['name', action.name]).stop.timeout = setTimeout(() => {
				// Get the current state.
				const newNextState = _.assign({}, this.state);
				// Reset this action
				newNextState.actions[type][action.name] = undefined;
				// Set the new state.
				this.setState(newNextState);

				loop();

			}, action.stop.interval());

		} else {
			loop();
		}
	}

	stopActions(type, noSetState) {
		// Clear timeouts to prevent loops.
		this.actions[type].forEach((action) => {
			clearTimeout(_.find(this.actions[type], ['name', action.name]).start.timeout);
			if (action.stop) clearTimeout(_.find(this.actions[type], ['name', action.name]).stop.timeout);
		});

		if (!noSetState) {
			// Reset pet actions.
			const nextState = _.assign({}, this.state);
			nextState.actions[type] = {};
			this.setState(nextState);
		}
	}

	componentWillUnmount() {
		this.stopActions('pet', true);
		this.stopActions('idle', true);
		clearTimeout(this.timeouts.stopBeingPet);
	}

	getClassNames() {
		return getClassNames(this.state, this.staticClasses);
	}

	getName() {
		let name = '';

		if (_.get(this.props.name, 'full')) {
			name = this.props.name.full;
		} else if (_.isString(this.props.name)) {
			name = this.props.name;
		}

		return name;
	}

	startBeingPet() {
		// Return without doing anything if we're
		// not allowed to pet this animal.
		if (this.props.disablePetting) return;

		// Prevent a stopBeingPet from completing if petting should resume.
		clearTimeout(this.timeouts.stopBeingPet);

		// If isBeingPet is already true, don't run this again.
		if (this.state.universalActions.isBeingPet) {
			return;
		}

		// Set petting to true
		const nextState = _.assign({}, this.state);
		nextState.universalActions.isBeingPet = true;
		this.setState(nextState);

		// Stop idle actions and Start pet actions.
		this.stopActions('idle');

		// Loop pet actions if it likes being pet.
		if (this.state.universalActions.likesPets) {
			this.loopAllActions('pet');
		}
	}

	stopBeingPet() {
		// Return without doing anything if we're
		// not allowed to pet this animal.
		if (this.props.disablePetting) return;

		this.timeouts.stopBeingPet = setTimeout(() => {
			// Set petting to false
			const nextState = _.assign({}, this.state);
			nextState.universalActions.isBeingPet = false;
			this.setState(nextState);

			// Stop pet actions and Start idle actions.
			this.stopActions('pet');
			this.loopAllActions('idle');
		}, 300);
	}

	getGlassesTargetCoords(targetElement) {
		this.setState({ glassesTargetCoords: {
			y: targetElement.cy.baseVal.value,
			x: targetElement.cx.baseVal.value,
		} });
	}

	getHatTargetCoords(targetElement) {
		this.setState({ hatTargetCoords: {
			y: targetElement.cy.baseVal.value,
			x: targetElement.cx.baseVal.value,
		} });
	}

	shouldRenderGlasses() {
		if (!this.props.glasses) {
			return null;
		}
		return (
			<Glasses
				petType={this.props.petType}
				goody={this.props.glasses}
				targetCoords={this.state.glassesTargetCoords}
			/>
		);
	}

	shouldRenderHat() {
		if (!this.props.hat) {
			return null;
		}
		return (
			<Hat
				petType={this.props.petType}
				goody={this.props.hat}
				targetCoords={this.state.hatTargetCoords}
			/>
		);
	}
}

PetHOC.defaultProps = INITIAL_STATES.PET;

PetHOC.propTypes = {
	petType: PropTypes.shape({
		type: PropTypes.string,
		name: PropTypes.string,
	}),
	name: PropTypes.shape({
		first: PropTypes.string,
		last: PropTypes.string,
		full: PropTypes.string.isRequired,
		special: PropTypes.string,
	}).isRequired,
	color: PropTypes.string.isRequired,
	likesPets: PropTypes.oneOf(getRandom.options.likesPetsOptions).isRequired,
	body: PropTypes.node.isRequired,
	head: PropTypes.node.isRequired,
	toy: PropTypes.string,
	glasses: PropTypes.string,
	hat: PropTypes.string,
	kitten: PropTypes.string,
	disablePetting: PropTypes.bool,
};

export default PetHOC;



// WEBPACK FOOTER //
// ./src/components/Global/Pet/PetHOC.js